import { isNodeEdge, NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	DateTime,
	dateTrunc,
	getDateTimeRange,
	isSameTime,
	isString,
	KeyWithValueType,
	stringToLocalDate,
	toLocalDateString,
} from "@hex-insights/core";
import { CalendarEventUtilities, SharingRecordUtilities } from "../../Models/Utilities";
import { CalendarEventFilterInput } from "../../Utilities";
import { EventCalendarItem } from "./types";

export function getEventGroupName(event: Pick<EventCalendarItem, "isPublic" | "groups" | "author">) {
	return CalendarEventUtilities.groupName({
		...event,
		sharingRecord: { isPublic: event.isPublic, userGroups: event.groups },
	});
}

export function getEventColor(event: Pick<EventCalendarItem, "isPublic" | "groups">) {
	return CalendarEventUtilities.color({
		...event,
		sharingRecord: { isPublic: event.isPublic, userGroups: event.groups },
	});
}

export function getCalendarEventFiltersForViewerForDateRange(
	startDate: DateTime,
	endDate: DateTime,
	truncateWeek = false,
): CalendarEventFilterInput {
	const startTimeStr = dateTrunc(
		isString(startDate) ? stringToLocalDate(startDate, "day") : startDate,
		truncateWeek ? "week" : "day",
	).toISOString();
	const endTimeStr = truncateWeek
		? addTimeToDate(
				dateTrunc(addTimeToDate(isString(endDate) ? stringToLocalDate(endDate, "day") : endDate, [1, "day"]), "week"),
				[1, "week"],
		  ).toISOString()
		: dateTrunc(
				addTimeToDate(isString(endDate) ? stringToLocalDate(endDate, "day") : endDate, [1, "day"]),
				"day",
		  ).toISOString();
	const startDateStr = toLocalDateString(startDate);
	const endDateStr = toLocalDateString(addTimeToDate(endDate, [1, "day"]));

	return {
		and: [
			{
				// user-related
				or: [
					{
						author: [{ isCurrentEQ: true }],
					},
					{
						sharingRecord: [SharingRecordUtilities.viewerSharingRecordFilter],
					},
				],
			},
			{
				// time-related
				or: [
					// timed events
					{
						startTimeGTE: startTimeStr,
						startTimeLT: endTimeStr,
					},
					{
						endTimeGTE: startTimeStr,
						endTimeLT: endTimeStr,
					},
					{
						startTimeLT: startTimeStr,
						endTimeGTE: endTimeStr,
					},
					// all-day events
					{
						startDateGTE: startDateStr,
						startDateLT: endDateStr,
					},
					{
						endDateGTE: startDateStr,
						endDateLT: endDateStr,
					},
					{
						startDateLT: startDateStr,
						endDateGTE: endDateStr,
					},
				],
			},
		],
	};
}

// Move to utils

export function datedItemsToDateMap<T extends Record<any, any>>(
	items: (T | NodeEdge<T>)[],
	dateFieldName: KeyWithValueType<T, DateTime | null> | ((item: T) => KeyWithValueType<T, DateTime | null>),
	isDayPrecision: boolean | ((item: T) => boolean),
) {
	return rangeDatedItemsToDateMap(items, dateFieldName, dateFieldName, isDayPrecision);
}

export function rangeDatedItemsToDateMap<T extends Record<any, any>>(
	items: (T | NodeEdge<T>)[],
	startFieldName: KeyWithValueType<T, DateTime | null> | ((item: T) => KeyWithValueType<T, DateTime | null>),
	endFieldName: KeyWithValueType<T, DateTime | null> | ((item: T) => KeyWithValueType<T, DateTime | null>),
	isDayPrecision: boolean | ((item: T) => boolean),
) {
	const dateItemsMap: Record<string, T[]> = {};
	for (let i = 0; i < items.length; i++) {
		const itemOrItemNode = items[i];
		const item = isNodeEdge(itemOrItemNode) ? itemOrItemNode.node : itemOrItemNode;
		const isItemDayPrecision = typeof isDayPrecision === "function" ? isDayPrecision(item) : isDayPrecision;
		const rawStart = item[typeof startFieldName === "function" ? startFieldName(item) : startFieldName];
		const rawEnd = item[typeof endFieldName === "function" ? endFieldName(item) : endFieldName];
		const start = isString(rawStart) ? stringToLocalDate(rawStart, isItemDayPrecision ? "day" : "second") : rawStart;
		const end = isString(rawEnd) ? stringToLocalDate(rawEnd, isItemDayPrecision ? "day" : "second") : rawEnd;
		if (start === null || end === null) {
			continue;
		}

		let dateRange = [];
		if (isItemDayPrecision) {
			if (isSameTime(start, end, "day")) {
				dateRange = [toLocalDateString(start)];
			} else {
				dateRange = getDateTimeRange(start, addTimeToDate(end, [1, "day"]));
			}
		} else {
			const startDate = dateTrunc(start, "day");
			const endDate = dateTrunc(end, "day");
			if (isSameTime(startDate, endDate, "day")) {
				dateRange = [toLocalDateString(startDate)];
			} else {
				dateRange = getDateTimeRange(startDate, addTimeToDate(endDate, [1, "day"]));
			}
		}

		for (let j = 0; j < dateRange.length; j++) {
			const date = dateToDateMapKey(dateRange[j]);
			if (!dateItemsMap[date]) {
				dateItemsMap[date] = [];
			}
			dateItemsMap[date].push(item);
		}
	}

	return dateItemsMap;
}

export function dateToDateMapKey(date: DateTime) {
	if (isString(date)) {
		return toLocalDateString(stringToLocalDate(date, "day"));
	}
	return toLocalDateString(date);
}
